import {
	Button,
	MenuDivider,
	MenuGroupHeader,
	MenuItemCheckbox,
	mergeClasses,
} from "@fluentui/react-components";
import { Filter20Regular, Search24Regular } from "@fluentui/react-icons";
import { InteractionMenuProps } from "./types";
import { useInteractionMenuStyle } from "./style";
import { Flex } from "@/components/atoms/flex";
import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { MenuListPopover } from "@/components/atoms/menuList";
import { InteractionType } from "@/common/types";
import { InteractionsIconMapMedium } from "@/common/helpers";
import { AppCacheState, CacheKey, useAppCacheStore } from "@/common/stores/persistingStores/app";
import { useInteractionStyle } from "@/components/organisms/interaction/style";
import { addBreadcrumb } from "@/common/helpers/sentryHelper";
import { ClearableInput } from "@/components/atoms/clearableInput";
import { QueueFilter } from "@/components/molecules/queueFilter/QueueFilter";

export const InteractionMenu = (props: InteractionMenuProps) => {
	const {
		incomingTypes,
		searchText,
		onTypeFilterChange,
		onQueueFilterChange,
		onSearchChange,
		className,
	} = props;
	const filterKey = "filter";
	const classes = useInteractionMenuStyle();
	const interactionClasses = useInteractionStyle();
	const intl = useIntl();

	const appStoreCache = useAppCacheStore((state: AppCacheState) => state.cache);
	const [selectedInteractions, setSelectedInteractions] = useState<
		InteractionType[]
	>(appStoreCache[CacheKey.InteractionTypeFilterValues] ?? []);

	const InteractionQueueIdsCacheKey: CacheKey = CacheKey.InteractionQueueIds;

	const interactionName = useCallback(
		(type: InteractionType) =>
			intl.formatMessage({ id: `Interaction.${InteractionType[type]}` }),
		[intl]
	);

	const onCheckedValueChangeCallback = useCallback(
		(_e: any, data: { checkedItems: string[] }) => {
			const selected = data.checkedItems.map(
				(key: string) => InteractionType[key as keyof typeof InteractionType]
			);

			addBreadcrumb("ui_interaction", "Interaction filters changed", {
				selectedFilters: selected,
			});

			setSelectedInteractions(selected);
			if (onTypeFilterChange) {
				onTypeFilterChange(selected);
			}
		},
		[setSelectedInteractions, onTypeFilterChange]
	);

	const onClearFilterClickCallback = useCallback(() => {
		addBreadcrumb("ui_interaction", "Interaction filters reset");
		setSelectedInteractions([]);
		if (onTypeFilterChange) {
			onTypeFilterChange([]);
		}
	}, [setSelectedInteractions, onTypeFilterChange]);

	const filterInteractionItems = useMemo<JSX.Element[]>(
		() => [
			<MenuGroupHeader
				key="interactionHeader"
				className={classes.filterGroupHeader}
			>
				{intl.formatMessage({ id: "InteractionMenu.ShowOnly" })}
				<Button
					appearance="transparent"
					className={classes.cleanButtonSize}
					onClick={onClearFilterClickCallback}
				>
					{intl.formatMessage({ id: "InteractionMenu.Reset" })}
				</Button>
			</MenuGroupHeader>,
			<MenuDivider key="interactionDivider" />,
			...incomingTypes.map((type: InteractionType) => (
				<MenuItemCheckbox
					key={type}
					name={filterKey}
					icon={InteractionsIconMapMedium[type]}
					value={InteractionType[type]}
					className={classes.filterCheckboxItem}
				>
					{interactionName(type)}
				</MenuItemCheckbox>
			)),
		],
		[intl, incomingTypes, interactionName, onClearFilterClickCallback]
	);

	return (
		<Flex className={className}>
			<ClearableInput
				clearable
				value={searchText}
				className={classes.searchInput}
				placeholder={intl.formatMessage({ id: "InteractionMenu.Search" })}
				contentAfter={<Search24Regular />}
				onChange={onSearchChange}
			/>

			<MenuListPopover
				classesName={{ menuList: classes.popoverContent }}
				positioning="above-end"
				checkedValues={{
					[filterKey]: (
						appStoreCache[CacheKey.InteractionTypeFilterValues] ?? []
					).map((x: any) => InteractionType[x]),
				}}
				onCheckedValueChange={onCheckedValueChangeCallback}
				trigger={
					selectedInteractions.length > 0 ? (
						<Button
							shape="circular"
							onClick={(e) => {
								e.preventDefault();
							}}
							className={mergeClasses(
								interactionClasses.filterButton,
								classes.filterTypeButton
							)}
						>
							<div className={interactionClasses.filterFontWeight}>
								{selectedInteractions.length}
							</div>
							<Filter20Regular
								className={interactionClasses.filterButtonIcon}
							/>
						</Button>
					) : (
						<Button
							appearance="transparent"
							className={mergeClasses(
								classes.cleanButtonSize,
								classes.menuButton,
							)}
							icon={<Filter20Regular />}
						/>
					)
				}
				items={filterInteractionItems}
			/>

			<QueueFilter
				cacheKey={InteractionQueueIdsCacheKey}
				onSelectedQueuesChange={onQueueFilterChange}
			/>
		</Flex>
	);
};
